import ExerciseHelpers from "helpers/ExerciseHelpers";
import http from "axiosSetup";
import type { Exercise, ExerciseMetric, ExerciseRepetition } from "types";

class ExerciseMetadataService {
  /**
   * Fetches the list of exercises from the API.
   * Filters out exercises that are not enabled (i.e., `isEnabled` is false).
   *
   * @important Custom exercises take the minimum velocity threshold of the exercise they were derived from.
   * This is currently a workaround to avoid looking up the parent exercise's minimum velocity threshold multiple times.
   */
  static async get(): Promise<Exercise[]> {
    return http.get<Exercise[]>("/exercises").then((response) => {
      const exercisesById = new Map(response.data.map((exercise) => [exercise.id, exercise]));

      return response.data
        .filter((exercise) => exercise.isEnabled !== false)
        .map((exercise) => ExerciseHelpers.inheritMinimumVelocityThreshold(exercise, (id) => exercisesById.get(id)));
    });
  }

  static defaultMetric(exercise: Pick<Exercise, "metrics"> | undefined): ExerciseMetric {
    return exercise?.metrics?.find((metric) => metric.isDefault) || ({} as ExerciseMetric);
  }

  static metric(
    exercise: Pick<Exercise, "metrics"> | undefined,
    metricName: string | undefined | null
  ): ExerciseMetric {
    return exercise?.metrics?.find((metric) => metric.field === metricName) || ({} as ExerciseMetric);
  }

  static repMetricByFieldNameOrFirstOrNull(
    exercise: Pick<Exercise, "repetitions"> | undefined,
    repMetricName: string | undefined | null
  ): ExerciseRepetition | null {
    const repMetric =
      exercise?.repetitions?.find((metric) => metric.field === repMetricName) ||
      exercise?.repetitions?.find((metric) => metric);

    return repMetric || null;
  }
}
export default ExerciseMetadataService;
